import { Controller } from 'stimulus';
import List from 'list.js';

// Single-selection Track left selector: a fixed "Overall Event" row plus a
// searchable/sortable Activities list. list.js manages ONLY the Activities list
// (the Overall Event row lives outside it, so it is never filtered or reordered).
//
// Search term + sort direction persist for the current browser session
// (sessionStorage, keyed by event) so returning to Track restores the working
// view without leaking across events or persisting forever.
const SESSION_KEY = 'activityFilter';

export default class extends Controller {
  static values = { eventId: Number };
  static targets = ['input', 'clear', 'sortButton', 'list'];

  connect() {
    this.filter = this.getFilter(this.eventIdValue);

    this.list = new List(this.listTarget, {
      listClass: 'list',
      valueNames: ['name', { data: ['index'] }]
    });

    // Restore the persisted search term (Activities only).
    this.inputTarget.value = this.filter.search || '';
    if (this.filter.search) {
      this.list.search(this.filter.search, ['name']);
    }
    this.updateClear();

    // Restore / apply the sort direction (default: ascending activity order).
    this.applySort();
  }

  getFilter(eventId) {
    let persisted = null;
    try {
      persisted = JSON.parse(sessionStorage.getItem(SESSION_KEY));
    } catch (e) {
      persisted = null;
    }
    if (persisted && persisted.eventId === eventId) {
      return persisted;
    }
    return { eventId, search: '', sortOrder: 'asc' };
  }

  handleInput(e) {
    this.filter.search = e.target.value;
    this.list.search(this.filter.search, ['name']);
    this.updateClear();
    this.persist();
  }

  clear(e) {
    if (e) e.preventDefault();
    this.filter.search = '';
    this.inputTarget.value = '';
    this.list.search();
    this.updateClear();
    this.inputTarget.focus();
    this.persist();
  }

  toggleSort(e) {
    if (e) e.preventDefault();
    this.filter.sortOrder = this.filter.sortOrder === 'asc' ? 'desc' : 'asc';
    this.applySort();
    this.persist();
  }

  applySort() {
    const dir = this.filter.sortOrder === 'desc' ? -1 : 1;
    // Numeric sort on the activity order/index; direction handled here (not via
    // list.js order) so it is deterministic regardless of list.js internals.
    this.list.sort('index', {
      order: 'asc',
      sortFunction: (a, b) => dir * (parseInt(a.values().index, 10) - parseInt(b.values().index, 10))
    });
    if (this.hasSortButtonTarget) {
      this.sortButtonTarget.classList.toggle('is-descending', this.filter.sortOrder === 'desc');
    }
  }

  updateClear() {
    if (!this.hasClearTarget) return;
    this.clearTarget.classList.toggle('is-hidden', !this.inputTarget.value);
  }

  persist() {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.filter));
    } catch (e) {
      /* sessionStorage unavailable (private mode quota) -- non-fatal */
    }
  }
}
